import { parseHexWithAlpha } from "@/layers/utility/color";
import type { ThresholdColor } from "@/overlay/components/CommonParts/Threshold/types";
import type { Expression } from "mapbox-gl";

/**
 * Converts an array of color thresholds into a Mapbox GL expression or a default color string.
 *
 * @param {string} defaultColor - The default color to use if thresholds are missing or invalid.
 * @param {ThresholdColor[]} thresholds - The array of color thresholds, or `undefined`.
 *   - Each `ColorThreshold` must have:
 *     - `condition`: A Mapbox GL expression representing the condition.
 *     - `value`: The value associated with the condition.
 *     - `color`: A 9-character hex color string (e.g., `#RRGGBBAA`) or a 7-character hex color (e.g., `#RRGGBB`).
 *
 * @returns {Expression | string} A Mapbox GL `Expression` if thresholds are valid, or the `defaultColor` string otherwise.
 *   - `Expression` format:
 *     - `"case"`: A conditional Mapbox GL expression.
 *     - Each threshold adds conditions and corresponding colors to the expression.
 *     - The fallback is the `defaultColor`.
 *   - `string`: Returns the `defaultColor` if no thresholds are provided or valid.
 *
 * @example
 * // With thresholds
 * const thresholds = [
 *   { condition: ["<", ["get", "value"], 10], value: 10, color: "#FF573380" },
 *   { condition: ["<", ["get", "value"], 20], value: 20, color: "#33FF5733" },
 * ];
 * const defaultColor = "#000000";
 * const expression = thresholdToMapboxExpression(defaultColor, thresholds);
 * // Returns:
 * // [
 * //   "case",
 * //   ["==", ["get", "value"], null], "#000000",
 * //   ["<", ["get", "value"], 10], "rgba(255, 87, 51, 0.5)",
 * //   ["<", ["get", "value"], 20], "rgba(51, 255, 87, 0.2)",
 * //   "#000000"
 * // ]
 *
 * @example
 * // Without thresholds
 * const expression = thresholdToMapboxExpression("#FFFFFF");
 * // Returns: "#FFFFFF"
 */
export const thresholdColorExpression = (
  defaultColor: string,
  thresholds?: ThresholdColor[],
  valueExpression: Expression = ["get", "value"],
): Expression | string => {
  //TODO: next update here to the new AdvancedThreshold
  if (!thresholds || !thresholds.length) {
    return parseHexWithAlpha(defaultColor);
  }
  const caseValue: Expression = ["case", ["==", valueExpression, null], defaultColor];
  for (const threshold of thresholds) {
    caseValue.push([threshold.condition, valueExpression, threshold.value]);
    caseValue.push(parseHexWithAlpha(threshold.color));
  }
  caseValue.push(parseHexWithAlpha(defaultColor) /*["get", "item-color"]*/);
  return caseValue as Expression;
};

export const thresholdColorExpressionFromProperties = (defaultColor: string): Expression => {
  const colorOrDefault: Expression = [
    "case",
    ["has", "threshold_color"],
    ["get", "threshold_color"],
    parseHexWithAlpha(defaultColor),
  ];

  return [
    "case",
    // Fallback
    ["==", ["get", "value"], null],
    parseHexWithAlpha(defaultColor),
    // >=
    ["all", ["==", ["get", "threshold_condition"], ">="], [">=", ["get", "value"], ["get", "threshold_value"]]],
    colorOrDefault,
    // >
    ["all", ["==", ["get", "threshold_condition"], ">"], [">", ["get", "value"], ["get", "threshold_value"]]],
    colorOrDefault,
    // <=
    ["all", ["==", ["get", "threshold_condition"], "<="], ["<=", ["get", "value"], ["get", "threshold_value"]]],
    colorOrDefault,
    // <
    ["all", ["==", ["get", "threshold_condition"], "<"], ["<", ["get", "value"], ["get", "threshold_value"]]],
    colorOrDefault,
    // ==
    ["all", ["==", ["get", "threshold_condition"], "=="], ["==", ["get", "value"], ["get", "threshold_value"]]],
    colorOrDefault,
    // !=
    ["all", ["==", ["get", "threshold_condition"], "!="], ["!=", ["get", "value"], ["get", "threshold_value"]]],
    colorOrDefault,
    // Fallback
    parseHexWithAlpha(defaultColor),
  ];
};
